import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Header from "../components/header/Header";
import Footer from "../components/footer";
import Gotop from "../components/gotop";
import { authAPI } from "../services/api";
import "./VerifyEmail.scss";

function VerifyEmail() {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState("verifying"); // verifying, success, error
  const [message, setMessage] = useState("");
  const hasVerifiedRef = useRef(false); // Use ref to prevent multiple API calls (refs don't cause re-renders)

  useEffect(() => {
    // Prevent multiple verification attempts
    if (hasVerifiedRef.current) {
      return;
    }

    // Mark as verified immediately to prevent duplicate calls (even in React Strict Mode)
    hasVerifiedRef.current = true;

    // Extract token from URL query string inside effect to avoid stale closures
    const params = new URLSearchParams(location.search);
    const token = params.get("token");

    const verifyEmail = async () => {
      if (!token) {
        setStatus("error");
        setMessage("Invalid verification link. No token provided.");
        return;
      }

      try {
        const response = await authAPI.verifyEmail(token);
        if (response.data.success) {
          setStatus("success");
          setMessage("Your email has been verified successfully! You can now log in to your account.");
          
          // Redirect to login after 3 seconds
          setTimeout(() => {
            navigate("/login");
          }, 3000);
        } else {
          setStatus("error");
          setMessage(response.data.message || "Verification failed. Please try again.");
        }
      } catch (err) {
        setStatus("error");
        const errorMsg = err.response?.data?.message || "Invalid or expired verification link. Please request a new verification email.";
        setMessage(errorMsg);
      }
    };

    verifyEmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount - token extracted inside to avoid stale closures

  const handleResendVerification = async () => {
    // We need email to resend, but we don't have it from the token
    // Redirect to login where user can enter email and resend
    navigate("/login");
  };

  return (
    <>
      <Header />
      <section className="verify-email-section">
        <div className="verify-email-container">
          <div className="verify-email-content">
            {status === "verifying" && (
              <>
                <div className="verify-email-icon verifying">
                  <div className="app-spinner" />
                </div>
                <h1>Verifying Your Email</h1>
                <p>Please wait while we verify your email address...</p>
              </>
            )}

            {status === "success" && (
              <>
                <div className="verify-email-icon success">
                  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="32" cy="32" r="32" fill="url(#successGradient)" />
                    <path d="M20 32L28 40L44 24" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                    <defs>
                      <linearGradient id="successGradient" x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
                        <stop stopColor="var(--secondary)" />
                        <stop offset="1" stopColor="var(--secondary-dark)" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
                <h1>Email Verified Successfully!</h1>
                <p>{message}</p>
                <div className="verify-email-actions">
                  <button 
                    className="verify-email-btn-primary"
                    onClick={() => navigate("/login")}
                  >
                    Go to Login
                  </button>
                </div>
                <p className="verify-email-redirect">
                  Redirecting to login page in 3 seconds...
                </p>
              </>
            )}

            {status === "error" && (
              <>
                <div className="verify-email-icon error">
                  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="32" cy="32" r="32" fill="url(#errorGradient)" />
                    <path d="M32 20V32M32 40H32.01" stroke="white" strokeWidth="3" strokeLinecap="round" />
                    <circle cx="32" cy="48" r="2" fill="white" />
                    <defs>
                      <linearGradient id="errorGradient" x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#EF4444" />
                        <stop offset="1" stopColor="#DC2626" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
                <h1>Verification Failed</h1>
                <p>{message}</p>
                <div className="verify-email-actions">
                  <button 
                    className="verify-email-btn-primary"
                    onClick={handleResendVerification}
                  >
                    Request New Verification Email
                  </button>
                  <button 
                    className="verify-email-btn-secondary"
                    onClick={() => navigate("/login")}
                  >
                    Go to Login
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </section>
      <Footer />
      <Gotop />
    </>
  );
}

export default VerifyEmail;
